import * as Sentry from '@sentry/nextjs';

import type {
  DbFailure,
  DbFailureReporter,
} from '@kit/supabase/db-failure-reporter';

import {
  ALWAYS_BUG_POSTGREST_CODES,
  isAlwaysBugDbError,
} from '../services/db-error-classification';

/**
 * The Sentry side of transport-level database reporting (BAL-1144).
 *
 * `@kit/supabase` observes every failed call and hands it here; this module
 * decides what is worth an event. Scope is "bugs + outages" (Antoine,
 * 2026-10-07): timeouts, network failures, 5xx, and the always-bug 4xx
 * classes. RLS denials, 401, 404, 406 and 409 are expected outcomes and
 * produce nothing.
 *
 * This is the single capture point for a returned database error:
 * `BaseService.handleDatabaseError` and `failDancerListRead` no longer
 * capture it themselves.
 */

const WINDOW_MS = 60_000;
const MAX_EVENTS_PER_WINDOW = 5;
/** Past this many fingerprints, expired windows are dropped from memory. */
const PRUNE_AT = 100;

/**
 * A SQLSTATE or a PostgREST code. `code` comes from the response body, and a
 * custom PostgREST error can put any text there, so anything else is sent as
 * `unrecognised` rather than verbatim.
 */
const CODE_SHAPE = /^(?:[0-9A-Z]{5}|PGRST\d{3})$/;

function safeCode(code: string | null): string | null {
  if (code === null) return null;
  return CODE_SHAPE.test(code) ? code : 'unrecognised';
}

/**
 * Coarse wall-time buckets for a deadline rejection. Coarse on purpose: the
 * tag answers "on time or hugely late", and a raw millisecond value would mint
 * a tag value per event.
 */
function elapsedBucket(elapsedMs: number | undefined): string {
  if (elapsedMs === undefined) return 'none';
  if (elapsedMs < 5_000) return '<5s';
  if (elapsedMs < 10_000) return '5-10s';
  if (elapsedMs < 60_000) return '10-60s';
  return '>=60s';
}

/**
 * A `stalled` failure is a runtime fact, not a database one: the deadline
 * fired late because the process was suspended or blocked, so it says nothing
 * about the table it happened to be reading. It gets ONE fingerprint for every
 * target. Grouped per target, as a timeout is, the same frozen instance minted
 * a separate warning for each table it had in flight (48 groups on
 * 2026-10-10, every one with 0 users).
 */
function eventFor(
  failure: DbFailure,
  code: string | null,
): { message: string; fingerprint: string[] } {
  const { kind, target, status } = failure;
  if (kind === 'stalled') {
    return {
      message: 'DB call outlived a stalled runtime: deadline fired late',
      fingerprint: ['db-transport', 'stalled'],
    };
  }
  return {
    message: `DB ${kind} on ${target}: ${code ?? status ?? kind}`,
    fingerprint: ['db-transport', target, code ?? String(status ?? kind)],
  };
}

function isReportable(failure: DbFailure, httpOnly: boolean): boolean {
  if (failure.kind !== 'http') return !httpOnly;

  const status = failure.status ?? 0;
  if (status >= 500) return true;
  if (status < 400) return false;

  if (failure.code && ALWAYS_BUG_POSTGREST_CODES.has(failure.code)) {
    return true;
  }

  return isAlwaysBugDbError({
    code: failure.code ?? undefined,
    message: failure.message ?? undefined,
  });
}

/**
 * @param options.now - injectable clock, for the throttle test.
 * @param options.httpOnly - report only failures the database answered with
 *   (`kind: 'http'`). The browser sets it: there a timeout or a network error
 *   is the visitor's connection (offline, a tunnel, a closed laptop), not an
 *   outage, and the event built here carries its own message, so the browser
 *   ignore list (`/abort/i`, `/load failed/`) never sees the original one.
 */
export function createSentryDbFailureReporter(
  options: { now?: () => number; httpOnly?: boolean } = {},
): DbFailureReporter {
  const now = options.now ?? Date.now;
  const httpOnly = options.httpOnly ?? false;
  // postgrest-js retries a 503 up to 4 times and an outage repeats per
  // request; Sentry groups by fingerprint but still bills each event.
  const windows = new Map<string, { windowStart: number; count: number }>();

  return (failure) => {
    if (!isReportable(failure, httpOnly)) return;

    const { kind, target, status, method, queryClass } = failure;
    const code = safeCode(failure.code);
    const { message, fingerprint } = eventFor(failure, code);

    const key = fingerprint.join('\u0000');
    const at = now();

    if (windows.size >= PRUNE_AT) {
      for (const [k, w] of windows) {
        if (at - w.windowStart >= WINDOW_MS) windows.delete(k);
      }
    }

    const window = windows.get(key);

    if (!window || at - window.windowStart >= WINDOW_MS) {
      windows.set(key, { windowStart: at, count: 1 });
    } else if (window.count >= MAX_EVENTS_PER_WINDOW) {
      return;
    } else {
      window.count += 1;
    }

    // R4: built only from validated fields. `failure.message` is read for
    // classification and never sent: a Postgres or PostgREST message can echo
    // row values (a custom RAISE, an identifier built from input), and a
    // network error message can carry the request URL.
    Sentry.captureException(new Error(message), {
      fingerprint,
      // Below the warning a database failure carries: the database was not
      // what failed, so this must not read as an outage.
      ...(kind === 'stalled' ? { level: 'info' as const } : {}),
      tags: {
        db_transport: 'true',
        db_target: target,
        db_code: code ?? 'none',
        db_status: String(status ?? 'none'),
        db_kind: kind,
        db_method: method,
        db_query_class: queryClass,
        db_elapsed_bucket: elapsedBucket(failure.elapsedMs),
      },
    });
  };
}
